import { useServiceProxy } from 'hooks/kong/useServiceProxy'
import { v4 as uuid } from 'uuid'
import { useDispatch, useSelector } from 'react-redux'
import { selectCurrentUser, selectFormsState, selectTicket } from 'redux/appStore'
import { useLocations } from 'hooks/redux/useLocations'
import { DynamoEvents, EventTypes } from 'types/events'
import { TransformedVehicle } from 'types/ticket'
import { useCreateContact } from 'hooks/tickets'
import { config } from '../../config'
import { addTicketHistoryEvent } from 'redux/callHistory/callHistorySlice'
import { isEmpty, isNil } from 'lodash-es'
import getVehicleState from './eventsUtils'

type UseServiceSubmitParams = {
  /** the type of the event to be created */
  eventType: EventTypes
}

const softServices = [
  EventTypes.FUEL,
  EventTypes.TIRES,
  EventTypes.TIRES_CHANGE_SPARE,
  EventTypes.WINCH,
  EventTypes.LOCKOUT,
  EventTypes.JUMPSTART,
  EventTypes.TOW,
]

const nonProviderEvents = [
  EventTypes.ADDITIONAL,
  EventTypes.CALL,
  EventTypes.CALLBACK,
  EventTypes.DROP,
  EventTypes.ESCALATE,
  EventTypes.EXCHANGE,
  EventTypes.EXCHANGE_TURNDOWN,
  EventTypes.FEATURE,
  EventTypes.LAW,
  EventTypes.TRANSFER,
]

/**
 * Returns the PUT event serviceProxy response. This hook creates
 * an event that's referenced to the call.
 */
export default function useCreateServiceEvent<T extends DynamoEvents['attributes']>({ eventType }: UseServiceSubmitParams) {
  const serviceProxy = useServiceProxy()
  const createContact = useCreateContact()
  const dispatch = useDispatch()
  const { ticketNumber, vehicle, vehicleHistory, pickupInfo } = useSelector(selectTicket)
  const serviceLocations = useLocations()
  const currentUser = useSelector(selectCurrentUser)
  const { towExchange } = useSelector(selectFormsState) as any
  const towDirection = towExchange ? towExchange.towDirection : undefined
  const { rentalAgreementUrn, vehicleUrn } = config
  // passing an optional vehicle allows for the calling form to pass a vehicle from form values
  async function createServiceEvent(values: T, eventId?: string, selectedVehicle?: TransformedVehicle) {
    const secondaryVehicle = vehicleHistory.find((vehicle) => !isNil(vehicle) && !isEmpty(vehicle)) as TransformedVehicle
    const serviceId = values?.serviceCase?.serviceId
    const eventIdentifier = eventId ?? serviceId ?? uuid()
    let eventServiceType = values?.eventType ? values?.eventType : eventType
    const isDeliveryOnlyExchange = eventServiceType === EventTypes.EXCHANGE && towDirection && towDirection === 'branch'

    const createTimestamp = new Date().toISOString()
    const rentalContext = rentalAgreementUrn + ticketNumber
    const vehicleContext = selectedVehicle
      ? vehicleUrn + selectedVehicle!.unitNumber
      : // If the primary vehicle is null ( find the first vehicle in vehicle history which should be the primary vehicle )
        isNil(vehicle)
        ? vehicleUrn + secondaryVehicle.unitNumber
        : vehicleUrn + vehicle!.unitNumber
    const audit = [
      {
        eId: currentUser?.nameID || '',
        createTimestamp,
      },
    ]
    const vehicleState = getVehicleState(serviceLocations, values)

    const provider = nonProviderEvents.includes(eventServiceType) ? undefined : values?.provider?.toLowerCase()
    const body = {
      audit,
      eventIdentifier,
      createTimestamp,
      eventType: eventServiceType,
      rentalContext,
      vehicleContext,
      serviceLocations,
      provider,
      attributes: {
        ...values,
        deliveryOnly: isDeliveryOnlyExchange,
        // This is used for RSA reporting in Splunk - events created by group branch
        rentalGroupBranchId: pickupInfo.groupBranchId,
      },
      vehicleState,
    }

    const event = await serviceProxy<DynamoEvents>('PUT', '/serviceproxy/events-table/items', {}, body)
    await createContact(eventIdentifier)

    dispatch(addTicketHistoryEvent({ event: event.data }))

    return event
  }

  return createServiceEvent
}
